import React, { useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, Box, Button, TextField, Divider, List, ListItem,
  ListItemText, Chip, IconButton, Tooltip
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import billingApi from '../services/billingApi';

const FRONTEND_URL = window.location.origin;

export default function ReferralCard() {
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [friendEmail, setFriendEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);

  useEffect(() => { loadReferral(); }, []);

  function loadReferral() {
    billingApi.get('/api/discounts/my-referral')
      .then(data => setReferral(data.referral))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  async function handleInvite(e) {
    e.preventDefault();
    if (!friendEmail.trim() || !friendEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await billingApi.post('/api/discounts/invite-friend', { email: friendEmail.trim() });
      setFriendEmail('');
      loadReferral(); // refresh to show updated invites + code
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  function copyToClipboard(text, type) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  function handleShare() {
    const shareLink = `${FRONTEND_URL}/plans?ref=${referral.code}`;
    if (navigator.share) {
      navigator.share({
        title: 'Join me on Raike & Sons',
        text: `Use my referral code ${referral.code} to get a discount!`,
        url: shareLink
      }).catch(() => {});
    } else {
      copyToClipboard(shareLink, 'link');
    }
  }

  if (loading) return null;

  const shareLink = referral ? `${FRONTEND_URL}/plans?ref=${referral.code}` : '';

  return (
    <Card variant="outlined" sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Share with a Friend
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your friend's email to get your personal referral code. Share the code or link however you like — text, email, social media.
        </Typography>

        {/* Invite form */}
        <form onSubmit={handleInvite}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="friend@example.com"
              type="email"
              value={friendEmail}
              onChange={e => { setFriendEmail(e.target.value); setError(''); }}
              sx={{ flex: 1 }}
              error={!!error}
              helperText={error || undefined}
            />
            <Button
              type="submit"
              variant="contained"
              size="small"
              startIcon={<PersonAddIcon />}
              disabled={submitting || !friendEmail.trim()}
            >
              {submitting ? 'Adding...' : 'Add Friend'}
            </Button>
          </Box>
        </form>

        {/* Show code + link after first invite */}
        {referral && (
          <>
            <Divider sx={{ my: 1.5 }} />

            <Typography variant="subtitle2" sx={{ mb: 1 }}>Your Referral Code</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
              <TextField
                value={referral.code}
                size="small"
                InputProps={{ readOnly: true, sx: { fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontWeight: 700, letterSpacing: 1 } }}
                sx={{ flex: 1, maxWidth: 250 }}
              />
              <Tooltip title={copied === 'code' ? 'Copied!' : 'Copy code'}>
                <IconButton size="small" onClick={() => copyToClipboard(referral.code, 'code')} aria-label="Copy referral code">
                  {copied === 'code' ? <CheckCircleIcon color="success" fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title={copied === 'link' ? 'Copied!' : 'Copy shareable link'}>
                <IconButton size="small" onClick={() => copyToClipboard(shareLink, 'link')} aria-label="Copy shareable link">
                  {copied === 'link' ? <CheckCircleIcon color="success" fontSize="small" /> : <ShareIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Button size="small" onClick={handleShare}>Share</Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Link: <code style={{ fontSize: 11 }}>{shareLink}</code>
            </Typography>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 3, mb: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Invited</Typography>
                <Typography variant="h6">{referral.invites?.length || 0}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Signed Up</Typography>
                <Typography variant="h6">{referral.total_referrals}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Converted</Typography>
                <Typography variant="h6">{referral.successful_referrals}</Typography>
              </Box>
            </Box>

            {/* Invite list */}
            {referral.invites && referral.invites.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary">Friends Invited</Typography>
                <List dense sx={{ py: 0 }}>
                  {referral.invites.map((inv, i) => (
                    <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                      <ListItemText
                        primary={inv.email}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                      <Chip
                        label={inv.converted ? 'Converted' : 'Pending'}
                        size="small"
                        color={inv.converted ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
